import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Button, 
  Statistic, 
  Row, 
  Col, 
  Typography, 
  Space, 
  Modal,
  Form,
  InputNumber,
  Switch,
  notification,
  Progress,
  Tabs,
  Alert,
  Divider,
  Tooltip,
  App,
  Empty
} from 'antd';
import { 
  PlayCircleOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
  NotificationOutlined,
  SoundOutlined,
  CheckCircleOutlined,
  RestOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  CloseOutlined,
  RightOutlined,
  ClockCircleOutlined,
  CoffeeOutlined
} from '@ant-design/icons';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../api/firebase';
import {
  savePomodoroSession,
  getTodayPomodoroSessions,
  savePomodoroSettings,
  getPomodoroSettings,
  resetPomodoroSettings,
  getPomodoroSettingsSummary
} from '../../api/services/pomodoroService';
import notificationSound from '../../assets/sounds/notification.mp3';

const { Title, Text } = Typography;
const { confirm } = Modal;

// Pomodoro Progress Flow Component
const PomodoroProgressFlow = ({ 
  sessionCount, 
  longBreakInterval, 
  currentSessionType, 
  isActive,
  settings,
  onCycleReset,
  onCycleComplete 
}) => {
  // Calculate current position in the cycle
  const currentCyclePosition = sessionCount % longBreakInterval;
  const cycleProgress = [];
  
  // Helper function to format duration from settings
  const formatDuration = (timeObj) => {
    if (!timeObj) return '0min';
    const { hours, minutes, seconds } = timeObj;
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 && hours === 0) parts.push(`${seconds}s`); // Only show seconds if no hours
    return parts.join(' ') || '0min';
  };
  
  // Generate the cycle visualization
  for (let i = 0; i < longBreakInterval; i++) {
    const workPosition = i;
    const isWorkCompleted = workPosition < currentCyclePosition;
    const isCurrentWork = workPosition === currentCyclePosition && currentSessionType === 'work';
    
    // Work session
    cycleProgress.push({
      type: 'work',
      position: workPosition,
      completed: isWorkCompleted,
      current: isCurrentWork,
      icon: <ClockCircleOutlined />,
      label: `Work ${workPosition + 1}`
    });
    
    // Add break (except after the last work session)
    if (i < longBreakInterval - 1) {
      const isBreakCompleted = workPosition < currentCyclePosition;
      const isCurrentBreak = workPosition === currentCyclePosition - 1 && 
                             (currentSessionType === 'shortBreak' || currentSessionType === 'longBreak');
      
      cycleProgress.push({
        type: 'shortBreak',
        position: workPosition,
        completed: isBreakCompleted,
        current: isCurrentBreak,
        icon: <CoffeeOutlined />,
        label: 'Short Break'
      });
    }
  }
  
  // Add long break at the end
  const isCurrentLongBreak = currentSessionType === 'longBreak';
  
  cycleProgress.push({
    type: 'longBreak',
    position: longBreakInterval,
    completed: false,
    current: isCurrentLongBreak,
    icon: <RestOutlined />,
    label: 'Long Break'
  });
  
  const getStepColor = (step) => {
    if (step.completed) return '#52c41a';
    if (step.current) {
      if (step.type === 'work') return '#1890ff';
      if (step.type === 'shortBreak') return '#FFA500';
      if (step.type === 'longBreak') return '#722ed1';
    }
    return '#d9d9d9';
  };
  
  const getStepBackground = (step) => {
    if (step.completed) return '#f6ffed';
    if (step.current) {
      if (step.type === 'work') return '#e6f7ff';
      if (step.type === 'shortBreak') return '#fff7e6';
      if (step.type === 'longBreak') return '#f9f0ff';
    }
    return '#fafafa';
  };
  
  return (
    <Card 
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RightOutlined style={{ color: '#1890ff' }} />
            <span>Pomodoro Cycle Progress</span>
          </div>
          <Tooltip title="Reset entire Pomodoro cycle">
            <Button 
              size="small"
              icon={<ReloadOutlined />}
              onClick={onCycleReset}
              disabled={isActive}
              style={{ 
                backgroundColor: '#ff4d4f',
                borderColor: '#ff4d4f',
                color: 'white',
                minWidth: '32px',
                height: '32px',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </Tooltip>
        </div>
      }
      size="small"
      style={{ marginBottom: '16px' }}
    >
      <div style={{ padding: '8px 0' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '4px', 
          overflowX: 'auto', 
          paddingBottom: '8px',
          minHeight: '80px' // Ensure consistent height
        }}>
          {cycleProgress.map((step, index) => (
            <React.Fragment key={`${step.type}-${step.position}`}>
              <Tooltip title={step.label}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: '50px',
                    maxWidth: '60px',
                    padding: '6px 4px',
                    borderRadius: '8px',
                    background: getStepBackground(step),
                    border: `2px solid ${getStepColor(step)}`,
                    transition: 'all 0.3s ease',
                    opacity: step.completed || step.current ? 1 : 0.6,
                    cursor: 'pointer'
                  }}
                >
                  <div
                    style={{
                      fontSize: '14px',
                      color: getStepColor(step),
                      marginBottom: '4px',
                      transform: step.current && isActive ? 'scale(1.1)' : 'scale(1)',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    {step.completed ? <CheckCircleOutlined /> : step.icon}
                  </div>
                  <Text 
                    style={{ 
                      fontSize: '9px', 
                      color: getStepColor(step),
                      fontWeight: step.current ? 'bold' : 'normal',
                      textAlign: 'center',
                      lineHeight: '1.2'
                    }}
                  >
                    {step.type === 'work' ? `W${step.position + 1}` : 
                     step.type === 'shortBreak' ? 'SB' : 'LB'}
                  </Text>
                </div>
              </Tooltip>
              
              {/* Arrow connector (except for last item) */}
              {index < cycleProgress.length - 1 && (
                <RightOutlined 
                  style={{ 
                    color: '#d9d9d9', 
                    fontSize: '8px',
                    margin: '0 1px',
                    flexShrink: 0
                  }} 
                />
              )}
            </React.Fragment>
          ))}
        </div>
        
        {/* Progress summary */}
        <div style={{ marginTop: '12px', textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {currentCyclePosition === 0 && sessionCount > 0 ? (
              <span style={{ color: '#722ed1', fontWeight: 'bold' }}>
                🎉 Ready for Long Break! Complete the cycle with a {formatDuration(settings.longBreakDuration)} rest.
              </span>
            ) : (
              <>
                Progress: {currentCyclePosition}/{longBreakInterval} work sessions • 
                {longBreakInterval - currentCyclePosition} more until long break
              </>
            )}
          </Text>
        </div>
      </div>
    </Card>
  );
};

const PomodoroApp = () => {
  const [user] = useAuthState(auth);
  const { message: messageApi } = App.useApp();
  
  // Timer state
  const [timeLeft, setTimeLeft] = useState(0); // Start with 0 instead of default 25 minutes
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [sessionType, setSessionType] = useState('work'); // 'work', 'shortBreak', 'longBreak'
  const [sessionCount, setSessionCount] = useState(0);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [showSessionCompleteModal, setShowSessionCompleteModal] = useState(false);
  const [nextSessionType, setNextSessionType] = useState('work');
  
  // Settings state - Enhanced with Firebase sync
  const [settings, setSettings] = useState({
    workDuration: { hours: 0, minutes: 25, seconds: 0 },
    shortBreakDuration: { hours: 0, minutes: 5, seconds: 0 },
    longBreakDuration: { hours: 0, minutes: 15, seconds: 0 },
    longBreakInterval: 4,
    notificationsEnabled: false,
    soundEnabled: false,
    autoStartBreaks: true,
    autoStartSessions: false,
    currentStreak: 0, // Add current streak to settings
    todaySessionCount: 0 // Add today's session count to settings
  });
  
  // UI state
  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [todaySessions, setTodaySessions] = useState([]);
  const [activeTab, setActiveTab] = useState('timer');
  const [notificationPermission, setNotificationPermission] = useState(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const [isLoadingSettings, setIsLoadingSettings] = useState(true); // Start with true for initial load
  const [isLoadingTimer, setIsLoadingTimer] = useState(true); // New loading state for timer
  const [settingsSummary, setSettingsSummary] = useState(null);
  
  const intervalRef = useRef(null);
  const audioRef = useRef(null);

  // Load user settings and today's sessions on mount
  useEffect(() => {
    if (user) {
      loadUserSettings();
      loadTodaySessions();
      loadSettingsSummary();
    } else {
      // Reset loading states if no user
      setIsLoadingSettings(false);
      setIsLoadingTimer(false);
      setTimeLeft(25 * 60); // Default 25 minutes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Dependencies are managed manually to avoid infinite loops

  // Initialize audio with custom sound
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.src = notificationSound;
      audioRef.current.load();
    }
  }, []);

  // Timer effect
  useEffect(() => {
    if (isActive && !isPaused && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(time => time - 1);
      }, 1000);
    } else if (isActive && timeLeft === 0) {
      // Only trigger session complete if timer was actually running
      handleSessionComplete();
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isPaused, timeLeft]); // handleSessionComplete dependency managed manually

  // Helper function to convert time object to seconds
  const timeToSeconds = (timeObj) => {
    if (!timeObj || typeof timeObj !== 'object') {
      return 0;
    }
    const hours = parseInt(timeObj.hours) || 0;
    const minutes = parseInt(timeObj.minutes) || 0;
    const seconds = parseInt(timeObj.seconds) || 0;
    return (hours * 3600) + (minutes * 60) + seconds;
  };

  // Validate time duration (must be greater than 0)
  const validateTimeDuration = (timeObj) => {
    const totalSeconds = timeToSeconds(timeObj);
    return totalSeconds > 0;
  };

  const loadUserSettings = async () => {
    try {
      setIsLoadingSettings(true);
      setIsLoadingTimer(true);
      const userSettings = await getPomodoroSettings(user.uid);
      
      // Ensure all settings have valid values
      const validatedSettings = {
        ...userSettings,
        workDuration: userSettings.workDuration || { hours: 0, minutes: 25, seconds: 0 },
        shortBreakDuration: userSettings.shortBreakDuration || { hours: 0, minutes: 5, seconds: 0 },
        longBreakDuration: userSettings.longBreakDuration || { hours: 0, minutes: 15, seconds: 0 },
        longBreakInterval: userSettings.longBreakInterval || 4,
        notificationsEnabled: userSettings.notificationsEnabled || false,
        soundEnabled: userSettings.soundEnabled || false,
        autoStartBreaks: userSettings.autoStartBreaks !== undefined ? userSettings.autoStartBreaks : true,
        autoStartSessions: userSettings.autoStartSessions || false,
        currentStreak: userSettings.currentStreak || 0,
        todaySessionCount: userSettings.todaySessionCount || 0
      };
      
      setSettings(validatedSettings);
      setSessionCount(validatedSettings.todaySessionCount);
      
      // Update timer with the loaded settings immediately
      const newTimeLeft = timeToSeconds(validatedSettings.workDuration);
      setTimeLeft(newTimeLeft);
      
      console.log('✅ Settings loaded and timer updated:', newTimeLeft, 'seconds');
    } catch (error) {
      console.error('Error loading settings:', error);
      // Don't show error for new users - just use defaults
      if (error.code !== 'not-found') {
        messageApi.error('Failed to load settings, using defaults');
      }
      // Set default timer even on error
      setTimeLeft(25 * 60); // 25 minutes default
    } finally {
      setIsLoadingSettings(false);
      setIsLoadingTimer(false);
    }
  };

  const loadSettingsSummary = async () => {
    if (user) {
      try {
        const summary = await getPomodoroSettingsSummary(user.uid);
        setSettingsSummary(summary);
      } catch (error) {
        console.error('Error loading settings summary:', error);
        // Don't show error message for this - it's not critical
      }
    }
  };

  const loadTodaySessions = async () => {
    try {
      const sessions = await getTodayPomodoroSessions(user.uid);
      setTodaySessions(sessions);
      
      // Calculate streak from sessions
      calculateCurrentStreak(sessions);
    } catch (error) {
      console.error('Error loading today\'s sessions:', error);
    }
  };

  const calculateCurrentStreak = (sessions) => {
    // For now, use simple logic - streak = number of work sessions today
    const workSessions = sessions.filter(session => session.type === 'work');
    const todayCount = workSessions.length;
    
    // Update settings with current counts
    setSettings(prev => ({
      ...prev,
      currentStreak: todayCount,
      todaySessionCount: todayCount
    }));
    setSessionCount(todayCount);
  };

  const handleStart = () => {
    // Validate current session has valid duration
    const currentDuration = getCurrentSessionDuration();
    if (currentDuration <= 0) {
      messageApi.error('Please set a valid duration in settings before starting');
      setShowSettings(true);
      return;
    }

    if (!isActive) {
      setSessionStartTime(new Date());
    }
    setIsActive(true);
    setIsPaused(false);
  };

  const handlePause = () => {
    setIsPaused(true);
  };

  const handleReset = () => {
    confirm({
      title: 'Reset Timer?',
      content: 'Are you sure you want to reset the current session? Your progress will be lost.',
      okText: 'Yes, Reset',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk() {
        setIsActive(false);
        setIsPaused(false);
        setSessionStartTime(null);
        resetTimer();
        messageApi.success('Timer reset successfully');
      }
    });
  };

  const handleCycleReset = () => {
    confirm({
      title: 'Reset Entire Pomodoro Cycle?',
      content: (
        <div>
          <p>This will reset your entire Pomodoro cycle progress:</p>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Current session count will be reset to 0</li>
            <li>Cycle progress will start from beginning</li>
            <li>Current timer will be reset</li>
            <li>Today's session count will be updated</li>
          </ul>
          <p style={{ marginTop: 8, color: '#ff4d4f' }}>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Yes, Reset Cycle',
      cancelText: 'Cancel',
      okType: 'danger',
      width: 450,
      async onOk() {
        try {
          // Reset all cycle-related states
          setIsActive(false);
          setIsPaused(false);
          setSessionStartTime(null);
          setSessionCount(0);
          setSessionType('work');
          resetTimer();
          
          // Update Firebase with reset session count
          if (user) {
            const updatedSettings = {
              ...settings,
              currentStreak: 0,
              todaySessionCount: 0
            };
            await savePomodoroSettings(user.uid, updatedSettings);
            setSettings(updatedSettings);
            
            // Reload today's sessions to refresh UI
            loadTodaySessions();
          }
          
          messageApi.success('Pomodoro cycle reset successfully!');
        } catch (error) {
          console.error('Error resetting cycle:', error);
          messageApi.error('Failed to reset cycle');
        }
      }
    });
  };

  const resetTimer = () => {
    let duration;
    switch (sessionType) {
      case 'shortBreak':
        duration = timeToSeconds(settings.shortBreakDuration);
        break;
      case 'longBreak':
        duration = timeToSeconds(settings.longBreakDuration);
        break;
      default:
        duration = timeToSeconds(settings.workDuration);
    }
    setTimeLeft(Math.max(duration, 0)); // Ensure non-negative
  };

  const handleSessionComplete = async () => {
    console.log('🎯 Session completed:', { sessionType, sessionCount, settings });
    setIsActive(false);
    setIsPaused(false);
    
    // Save session to Firestore
    if (user && sessionStartTime) {
      try {
        await savePomodoroSession(user.uid, {
          start: sessionStartTime,
          duration: getCurrentSessionDuration(),
          type: sessionType
        });
        
        // Update session count and save to Firebase
        let newSessionCount = sessionCount;
        if (sessionType === 'work') {
          newSessionCount = sessionCount + 1;
          setSessionCount(newSessionCount);
          
          // Save updated counts to Firebase
          const updatedSettings = {
            ...settings,
            currentStreak: newSessionCount,
            todaySessionCount: newSessionCount
          };
          await savePomodoroSettings(user.uid, updatedSettings);
          setSettings(updatedSettings);
        }
        
        loadTodaySessions(); // Refresh today's sessions
      } catch (error) {
        console.error('Error saving session:', error);
      }
    }

    // Determine next session type with correct logic using CURRENT settings
    let nextType;
    if (sessionType === 'work') {
      // Use the new session count (current + 1) for long break calculation
      const totalWorkSessions = sessionCount + 1;
      console.log('📊 Checking long break logic:', { 
        totalWorkSessions, 
        longBreakInterval: settings.longBreakInterval,
        shouldBeLongBreak: totalWorkSessions % settings.longBreakInterval === 0 
      });
      
      nextType = totalWorkSessions % settings.longBreakInterval === 0 
        ? 'longBreak' 
        : 'shortBreak';
        
      console.log('📋 Next session type:', nextType);
    } else if (sessionType === 'longBreak') {
      // After long break, start a new cycle with work session
      nextType = 'work';
      console.log('🔄 Long break completed - starting new Pomodoro cycle');
    } else {
      // After short break, continue with work
      nextType = 'work';
    }
    setNextSessionType(nextType);

    // Show notification (only one, not duplicate)
    showNotification();

    // Play sound if enabled
    if (settings.soundEnabled) {
      playNotificationSound();
    }

    // Handle auto-start logic with CURRENT settings (reload settings to get latest)
    console.log('🔧 Auto-start check:', {
      sessionType,
      nextType,
      autoStartBreaks: settings.autoStartBreaks,
      autoStartSessions: settings.autoStartSessions,
      sessionCount,
      currentCyclePosition: sessionCount % settings.longBreakInterval,
      isAfterLongBreak: sessionType === 'longBreak'
    });

    if (sessionType === 'work' && settings.autoStartBreaks) {
      // Auto-start break (short or long)
      console.log('▶️ Auto-starting break:', nextType);
      setSessionType(nextType);
      resetTimerForType(nextType);
      setIsActive(true);
      setSessionStartTime(new Date());
    } else if (sessionType === 'shortBreak' && settings.autoStartSessions) {
      // Auto-start next work session after short break
      console.log('▶️ Auto-starting work session after short break');
      setSessionType(nextType);
      resetTimerForType(nextType);
      setIsActive(true);
      setSessionStartTime(new Date());
    } else if (sessionType === 'longBreak') {
      // After long break completion, always stop and require manual start (new cycle begins)
      console.log('🛑 Long break completed - cycle finished, manual start required');
      console.log('📊 Modal context:', {
        sessionType: 'longBreak',
        nextType,
        shouldShowNewCycleMessage: true,
        sessionCount,
        longBreakInterval: settings.longBreakInterval
      });
      setSessionType(nextType);
      resetTimerForType(nextType);
      setShowSessionCompleteModal(true);
    } else {
      // Show modal for manual start
      console.log('⏸️ Showing manual start modal');
      console.log('📊 Modal context:', {
        sessionType,
        nextType,
        sessionCount,
        cyclePosition: sessionCount % settings.longBreakInterval,
        isNewCycleStart: (sessionCount % settings.longBreakInterval === 0 && sessionCount > 0)
      });
      setShowSessionCompleteModal(true);
    }
  };

  const resetTimerForType = (type) => {
    let duration;
    switch (type) {
      case 'shortBreak':
        duration = timeToSeconds(settings.shortBreakDuration);
        break;
      case 'longBreak':
        duration = timeToSeconds(settings.longBreakDuration);
        break;
      default:
        duration = timeToSeconds(settings.workDuration);
    }
    setTimeLeft(Math.max(duration, 0)); // Ensure non-negative
  };

  const handleStartNextSession = () => {
    setSessionType(nextSessionType);
    resetTimerForType(nextSessionType);
    setShowSessionCompleteModal(false);
    // Don't auto-start, let user click start button
  };

  const handleSkipSession = () => {
    setSessionType(nextSessionType);
    resetTimerForType(nextSessionType);
    setShowSessionCompleteModal(false);
  };

  const getCurrentSessionDuration = () => {
    switch (sessionType) {
      case 'shortBreak':
        return timeToSeconds(settings.shortBreakDuration);
      case 'longBreak':
        return timeToSeconds(settings.longBreakDuration);
      default:
        return timeToSeconds(settings.workDuration);
    }
  };

  const showNotification = () => {
    const messageText = sessionType === 'work' 
      ? 'Work session complete! Time for a break.' 
      : 'Break time over! Ready to focus?';
    
    // Only show browser notification if enabled and permission granted
    if (settings.notificationsEnabled && notificationPermission === 'granted') {
      new Notification('🎯 Progress Craft Pomodoro', {
        body: messageText,
        icon: '/favicon.ico',
        tag: 'pomodoro-notification' // Prevents duplicates
      });
    }

    // Always show in-app notification
    notification.success({
      message: '🎯 Session Complete!',
      description: messageText,
      placement: 'topRight',
      duration: 4
    });
  };

  const toggleNotifications = async () => {
    try {
      if (!settings.notificationsEnabled) {
        // Request permission
        if ('Notification' in window) {
          const permission = await Notification.requestPermission();
          setNotificationPermission(permission);
          
          if (permission === 'granted') {
            const newSettings = { ...settings, notificationsEnabled: true };
            setSettings(newSettings);
            if (user) {
              await savePomodoroSettings(user.uid, newSettings);
              loadSettingsSummary();
            }
            messageApi.success('Notifications enabled successfully!');
          } else {
            messageApi.warning('Notification permission denied. You can enable it later in browser settings.');
          }
        } else {
          messageApi.error('Notifications are not supported in this browser');
        }
      } else {
        // Disable notifications
        const newSettings = { ...settings, notificationsEnabled: false };
        setSettings(newSettings);
        if (user) {
          await savePomodoroSettings(user.uid, newSettings);
          loadSettingsSummary();
        }
        messageApi.success('Notifications disabled');
      }
    } catch (error) {
      console.error('Error toggling notifications:', error);
      messageApi.error('Failed to update notification settings');
    }
  };

  const toggleSound = async () => {
    try {
      const newSettings = { ...settings, soundEnabled: !settings.soundEnabled };
      setSettings(newSettings);
      
      if (user) {
        await savePomodoroSettings(user.uid, newSettings);
        loadSettingsSummary();
        messageApi.success(`Sound ${newSettings.soundEnabled ? 'enabled' : 'disabled'}`);
        
        // Play test sound if enabling
        if (newSettings.soundEnabled) {
          playNotificationSound();
        }
      }
    } catch (error) {
      console.error('Error saving sound setting:', error);
      messageApi.error('Failed to save sound setting');
      // Revert on error
      setSettings(settings);
    }
  };

  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0; // Reset to beginning
      audioRef.current.play().catch(e => {
        console.log('Sound play failed:', e);
        messageApi.warning('Could not play notification sound');
      });
    }
  };

  const handleResetSettings = () => {
    confirm({
      title: 'Reset All Settings?',
      content: (
        <div>
          <p>This will reset all your Pomodoro settings to default values:</p>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Work Duration: 25 minutes</li>
            <li>Short Break: 5 minutes</li>
            <li>Long Break: 15 minutes</li>
            <li>Long Break Interval: 4 sessions</li>
            <li>Notifications: Disabled</li>
            <li>Sound: Disabled</li>
            <li>Auto-start breaks: Enabled</li>
            <li>Auto-start sessions: Disabled</li>
            <li>Session counts will be reset</li>
          </ul>
          <p style={{ marginTop: 8, color: '#ff4d4f' }}>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Yes, Reset All',
      cancelText: 'Cancel',
      okType: 'danger',
      width: 500,
      async onOk() {
        if (user) {
          try {
            setIsLoadingSettings(true);
            const defaultSettings = await resetPomodoroSettings(user.uid);
            setSettings(defaultSettings);
            setSessionCount(0);
            setTimeLeft(timeToSeconds(defaultSettings.workDuration));
            loadSettingsSummary();
            messageApi.success('Settings reset to defaults successfully!');
            setShowSettings(false);
          } catch (error) {
            console.error('Error resetting settings:', error);
            messageApi.error('Failed to reset settings');
          } finally {
            setIsLoadingSettings(false);
          }
        }
      }
    });
  };

  const handleSettingsSave = async (values) => {
    try {
      setIsLoadingSettings(true);
      setIsLoadingTimer(true); // Also show timer loading during save
      
      // Validate all time durations
      if (!validateTimeDuration(values.workDuration)) {
        messageApi.error('Work duration must be greater than 0');
        return;
      }
      if (!validateTimeDuration(values.shortBreakDuration)) {
        messageApi.error('Short break duration must be greater than 0');
        return;
      }
      if (!validateTimeDuration(values.longBreakDuration)) {
        messageApi.error('Long break duration must be greater than 0');
        return;
      }
      
      const newSettings = {
        ...values,
        notificationsEnabled: settings.notificationsEnabled,
        soundEnabled: settings.soundEnabled,
        currentStreak: settings.currentStreak,
        todaySessionCount: settings.todaySessionCount
      };
      
      setSettings(newSettings);
      
      if (user) {
        await savePomodoroSettings(user.uid, newSettings);
        loadSettingsSummary();
        messageApi.success('Settings saved successfully!');
        
        console.log('⚙️ Settings updated during session:', {
          newLongBreakInterval: newSettings.longBreakInterval,
          newAutoStartBreaks: newSettings.autoStartBreaks,
          newAutoStartSessions: newSettings.autoStartSessions,
          currentSessionActive: isActive
        });
      }
      
      // Immediately update timer with new settings based on current session type
      let newTimeLeft;
      switch (sessionType) {
        case 'shortBreak':
          newTimeLeft = timeToSeconds(newSettings.shortBreakDuration);
          break;
        case 'longBreak':
          newTimeLeft = timeToSeconds(newSettings.longBreakDuration);
          break;
        default:
          newTimeLeft = timeToSeconds(newSettings.workDuration);
      }
      
      // Only update timer if not currently active to avoid disrupting running session
      if (!isActive) {
        setTimeLeft(newTimeLeft);
        console.log('⏱️ Timer updated with new settings:', newTimeLeft, 'seconds');
      } else {
        console.log('⏱️ Timer not updated - session is active');
      }
      
      setShowSettings(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      messageApi.error('Failed to save settings');
    } finally {
      setIsLoadingSettings(false);
      setIsLoadingTimer(false);
    }
  };

  const showHelpModal = () => {
    setShowHelp(true);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getSessionColor = () => {
    // Use blue color for all active sessions to maintain consistency
    return '#1890ff';
  };

  const getSessionTitle = () => {
    switch (sessionType) {
      case 'shortBreak':
        return 'Short Break';
      case 'longBreak':
        return 'Long Break';
      default:
        return 'Focus Time';
    }
  };

  const progress = (isLoadingTimer || getCurrentSessionDuration() <= 0) 
    ? 0 
    : ((getCurrentSessionDuration() - timeLeft) / getCurrentSessionDuration()) * 100;

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <Title level={2} style={{ textAlign: 'center', color: '#000080', marginBottom: '32px' }}>
        Pomodoro Timer
      </Title>

      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab} 
        centered
        items={[
          {
            key: 'timer',
            label: 'Timer',
            children: (
              <Row gutter={[24, 24]}>
                <Col xs={24} lg={16}>
                  {/* Main Timer Card */}
                  <Card 
                    style={{ 
                      textAlign: 'center',
                      borderRadius: '16px',
                      background: `linear-gradient(135deg, ${getSessionColor()}10, ${getSessionColor()}05)`
                    }}
                  >
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      <Title level={3} style={{ color: getSessionColor(), margin: 0 }}>
                        {getSessionTitle()}
                      </Title>
                      
                      <Progress 
                        type="circle" 
                        percent={isLoadingTimer ? 0 : progress}
                        status={isLoadingTimer ? 'active' : undefined}
                        size={200}
                        strokeColor={getSessionColor()}
                        format={() => (
                          <div style={{ textAlign: 'center' }}>
                            {isLoadingTimer ? (
                              <>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                                  Loading...
                                </div>
                                <div style={{ fontSize: '12px', color: '#4B4B4B' }}>
                                  ⏳ Setting up timer
                                </div>
                              </>
                            ) : (
                              <>
                                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#000080' }}>
                                  {formatTime(timeLeft)}
                                </div>
                                <div style={{ 
                                  fontSize: '14px', 
                                  color: '#4B4B4B',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '4px',
                                  marginTop: '4px'
                                }}>
                                  {sessionType === 'work' ? (
                                    <>
                                      <ClockCircleOutlined style={{ color: getSessionColor() }} />
                                      <span>Focus</span>
                                    </>
                                  ) : sessionType === 'shortBreak' ? (
                                    <>
                                      <ClockCircleOutlined style={{ color: getSessionColor() }} />
                                      <span>Short Break</span>
                                    </>
                                  ) : (
                                    <>
                                      <ClockCircleOutlined style={{ color: getSessionColor() }} />
                                      <span>Long Break</span>
                                    </>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      />

                      <Space size="large">
                        {!isActive || isPaused ? (
                          <Button 
                            type="primary" 
                            size="large"
                            icon={<PlayCircleOutlined />}
                            onClick={handleStart}
                            disabled={isLoadingTimer}
                            loading={isLoadingTimer}
                            style={{ 
                              backgroundColor: isActive && isPaused ? '#FFA500' : '#1890ff', // Amber for Resume, Blue for Start
                              borderColor: isActive && isPaused ? '#FFA500' : '#1890ff',
                              height: '48px',
                              fontSize: '16px'
                            }}
                          >
                            {isActive ? 'Resume' : 'Start'}
                          </Button>
                        ) : (
                          <Button 
                            size="large"
                            icon={<PauseCircleOutlined />}
                            onClick={handlePause}
                            disabled={isLoadingTimer}
                            style={{ 
                              backgroundColor: '#FFA500', // Amber for Pause (changed from red)
                              borderColor: '#FFA500',
                              color: 'white',
                              height: '48px', 
                              fontSize: '16px' 
                            }}
                          >
                            Pause
                          </Button>
                        )}
                        
                        <Button 
                          size="large"
                          icon={<ReloadOutlined />}
                          onClick={handleReset}
                          disabled={isLoadingTimer}
                          style={{ 
                            backgroundColor: '#ff4d4f', // Red for Reset
                            borderColor: '#ff4d4f',
                            color: 'white',
                            height: '48px', 
                            fontSize: '16px' 
                          }}
                        >
                          Reset
                        </Button>
                      </Space>
                    </Space>
                  </Card>
                </Col>

                {/* Pomodoro Progress Flow - New component */}
                <Col xs={24}>
                  <PomodoroProgressFlow
                    sessionCount={sessionCount}
                    longBreakInterval={settings.longBreakInterval}
                    currentSessionType={sessionType}
                    isActive={isActive}
                    settings={settings}
                    onCycleReset={handleCycleReset}
                  />
                </Col>

                <Col xs={24} lg={8}>
                  {/* Stats Card */}
                  <Card title="Today's Progress" style={{ marginBottom: '16px' }}>
                    <Statistic 
                      title="Sessions Completed" 
                      value={todaySessions.length}
                      valueStyle={{ color: '#1E90FF' }}
                    />
                    <Statistic 
                      title="Completed Cycles" 
                      value={Math.floor(todaySessions.filter(session => session.type === 'work').length / settings.longBreakInterval)}
                      style={{ marginTop: '16px' }}
                      valueStyle={{ color: '#52c41a' }}
                    />
                    <Statistic 
                      title="Current Streak" 
                      value={settings.currentStreak}
                      style={{ marginTop: '16px' }}
                      valueStyle={{ color: '#FFA500' }}
                    />
                  </Card>

                  {/* Quick Controls Card */}
                  <Card title="Quick Controls">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {/* Top Row: Settings and Help */}
                      <Row gutter={8}>
                        <Col span={12}>
                          <Button 
                            icon={<SettingOutlined />}
                            onClick={() => setShowSettings(true)}
                            loading={isLoadingSettings}
                            block
                          >
                            Settings
                          </Button>
                        </Col>
                        <Col span={12}>
                          <Button 
                            icon={<QuestionCircleOutlined />}
                            onClick={showHelpModal}
                            block
                          >
                            Help
                          </Button>
                        </Col>
                      </Row>
                      
                      {/* Bottom Row: Notifications and Sound */}
                      <Row gutter={8}>
                        <Col span={12}>
                          <Tooltip title={settings.notificationsEnabled ? 'Click to disable browser notifications' : 'Click to enable browser notifications'}>
                            <Button 
                              icon={<NotificationOutlined />}
                              onClick={toggleNotifications}
                              block
                              size="small"
                              style={{
                                backgroundColor: settings.notificationsEnabled ? '#1890ff' : 'transparent',
                                borderColor: settings.notificationsEnabled ? '#1890ff' : '#d9d9d9',
                                color: settings.notificationsEnabled ? 'white' : 'inherit'
                              }}
                            >
                              {settings.notificationsEnabled ? 'Notifications ON' : 'Notifications OFF'}
                            </Button>
                          </Tooltip>
                        </Col>
                        <Col span={12}>
                          <Tooltip title={settings.soundEnabled ? 'Click to disable notification sounds' : 'Click to enable notification sounds'}>
                            <Button 
                              icon={<SoundOutlined />}
                              onClick={toggleSound}
                              block
                              size="small"
                              style={{
                                backgroundColor: settings.soundEnabled ? '#1890ff' : 'transparent',
                                borderColor: settings.soundEnabled ? '#1890ff' : '#d9d9d9',
                                color: settings.soundEnabled ? 'white' : 'inherit'
                              }}
                            >
                              {settings.soundEnabled ? 'Sound ON' : 'Sound OFF'}
                            </Button>
                          </Tooltip>
                        </Col>
                      </Row>

                      {/* Settings Summary */}
                      {settingsSummary && (
                        <div style={{ marginTop: '12px', padding: '8px', background: '#f5f5f5', borderRadius: '6px' }}>
                          <Text style={{ fontSize: '12px', color: '#666' }}>
                            <InfoCircleOutlined style={{ marginRight: '4px' }} />
                            Work: {settingsSummary.workDuration} | 
                            Break: {settingsSummary.shortBreakDuration} | 
                            Long: {settingsSummary.longBreakDuration}
                          </Text>
                        </div>
                      )}
                    </Space>
                  </Card>
                </Col>
              </Row>
            )
          },
          {
            key: 'analytics',
            label: 'Analytics',
            children: (
              <div>
                {/* Today's Session Breakdown */}
                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                  <Col xs={24} sm={12} lg={6}>
                    <Card size="small">
                      <Statistic
                        title="Work Sessions Today"
                        value={todaySessions.filter(session => session.type === 'work').length}
                        prefix={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
                        valueStyle={{ color: '#1890ff' }}
                        suffix="sessions"
                      />
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        Focus sessions completed
                      </Text>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Card size="small">
                      <Statistic
                        title="Completed Cycles"
                        value={Math.floor(todaySessions.filter(session => session.type === 'work').length / settings.longBreakInterval)}
                        prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                        valueStyle={{ color: '#52c41a' }}
                        suffix="cycles"
                      />
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        Full Pomodoro cycles done
                      </Text>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Card size="small">
                      <Statistic
                        title="Breaks Taken"
                        value={todaySessions.filter(session => session.type === 'shortBreak' || session.type === 'longBreak').length}
                        prefix={<CoffeeOutlined style={{ color: '#FFA500' }} />}
                        valueStyle={{ color: '#FFA500' }}
                        suffix="breaks"
                      />
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        Short + long breaks
                      </Text>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Card size="small">
                      <Statistic
                        title="Focus Time"
                        value={todaySessions
                          .filter(session => session.type === 'work')
                          .reduce((total, session) => {
                            if (session.duration && typeof session.duration === 'number') {
                              return total + Math.round(session.duration / 60);
                            }
                            return total;
                          }, 0)
                        }
                        prefix={<RightOutlined style={{ color: '#722ed1' }} />}
                        valueStyle={{ color: '#722ed1' }}
                        suffix="min"
                      />
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        Total focused time
                      </Text>
                    </Card>
                  </Col>
                </Row>

                {/* Session Timeline */}
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={12}>
                    <Card 
                      title="Today's Session Timeline" 
                      size="small"
                      style={{ height: '280px' }}
                    >
                      {todaySessions.length > 0 ? (
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {todaySessions
                            .sort((a, b) => new Date(b.createdAt?.seconds * 1000) - new Date(a.createdAt?.seconds * 1000))
                            .map((session, index) => (
                              <div 
                                key={index} 
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  padding: '8px 0',
                                  borderBottom: index < todaySessions.length - 1 ? '1px solid #f0f0f0' : 'none'
                                }}
                              >
                                <div style={{ marginRight: '12px' }}>
                                  {session.type === 'work' ? 
                                    <ClockCircleOutlined style={{ color: '#1890ff', fontSize: '16px' }} /> :
                                    session.type === 'longBreak' ?
                                    <RestOutlined style={{ color: '#722ed1', fontSize: '16px' }} /> :
                                    <CoffeeOutlined style={{ color: '#FFA500', fontSize: '16px' }} />
                                  }
                                </div>
                                <div style={{ flex: 1 }}>
                                  <Text strong style={{ fontSize: '13px' }}>
                                    {session.type === 'work' ? 'Work Session' : 
                                     session.type === 'longBreak' ? 'Long Break' : 'Short Break'}
                                  </Text>
                                  <br />
                                  <Text type="secondary" style={{ fontSize: '11px' }}>
                                    {session.duration ? Math.round(session.duration / 60) : 0} minutes • 
                                    {session.createdAt ? new Date(session.createdAt.seconds * 1000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'Unknown time'}
                                  </Text>
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      ) : (
                        <Empty 
                          description="No sessions today yet" 
                          style={{ padding: '40px 0' }}
                        />
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card 
                      title="Current Cycle Progress" 
                      size="small"
                      style={{ height: '280px' }}
                    >
                      <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ marginBottom: '16px' }}>
                          <Text style={{ fontSize: '48px', fontWeight: 'bold', color: getSessionColor() }}>
                            {sessionCount % settings.longBreakInterval}
                          </Text>
                          <Text style={{ fontSize: '16px', color: '#8c8c8c', display: 'block' }}>
                            / {settings.longBreakInterval}
                          </Text>
                        </div>
                        <Text style={{ fontSize: '14px', color: '#4B4B4B' }}>
                          Work sessions in current cycle
                        </Text>
                        
                        <div style={{ marginTop: '20px', padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                          <Text style={{ fontSize: '12px' }}>
                            <strong>Next:</strong> {sessionCount % settings.longBreakInterval === 0 && sessionCount > 0 ? 
                              `Long Break (${Math.round(timeToSeconds(settings.longBreakDuration) / 60)} min)` : 
                              sessionType === 'work' ? 
                                `Short Break (${Math.round(timeToSeconds(settings.shortBreakDuration) / 60)} min)` : 
                                `Work Session (${Math.round(timeToSeconds(settings.workDuration) / 60)} min)`
                            }
                          </Text>
                        </div>
                        
                        {todaySessions.length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              Average session: {Math.round(
                                todaySessions
                                  .filter(s => s.type === 'work' && s.duration)
                                  .reduce((sum, s) => sum + s.duration, 0) / 
                                (todaySessions.filter(s => s.type === 'work' && s.duration).length * 60) || 0
                              )} minutes
                            </Text>
                          </div>
                        )}
                      </div>
                    </Card>
                  </Col>
                </Row>
              </div>
            )
          }
        ]}
      />

      {/* Session Complete Modal */}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '24px', marginRight: '8px' }} />
            Session Complete!
          </div>
        }
        open={showSessionCompleteModal}
        onCancel={() => setShowSessionCompleteModal(false)}
        footer={null}
        centered
        width="min(85vw, 400px)"
        styles={{ 
          body: { 
            padding: '20px 16px',
            maxHeight: '80vh',
            overflowY: 'auto',
            overflowX: 'hidden'
          },
          content: {
            maxWidth: '400px',
            margin: '0 auto',
            borderRadius: '12px'
          },
          header: {
            padding: '16px 24px 8px',
            borderBottom: 'none'
          }
        }}
        maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Text style={{ fontSize: '16px', display: 'block', marginBottom: '24px' }}>
            {(() => {
              // Determine the actual context based on nextSessionType and cycle position
              const currentCyclePosition = sessionCount % settings.longBreakInterval;
              const isStartingNewCycle = sessionType === 'longBreak' || (currentCyclePosition === 0 && sessionCount > 0);
              
              if (isStartingNewCycle) {
                return '🎉 Pomodoro Cycle Complete! Ready to start a fresh cycle?';
              } else if (sessionType === 'work') {
                return 'Great work! Ready to take a break?';
              } else {
                return 'Break time is over! Ready to get back to work?';
              }
            })()}
          </Text>
          <Space size="large" direction="vertical" style={{ width: '100%' }}>
            <Button 
              type="primary" 
              size="large"
              onClick={handleStartNextSession}
              style={{ 
                backgroundColor: getSessionColor(), 
                borderColor: getSessionColor(),
                width: '100%',
                maxWidth: '300px',
                height: '48px',
                fontSize: '16px'
              }}
            >
              {(() => {
                const currentCyclePosition = sessionCount % settings.longBreakInterval;
                const isStartingNewCycle = sessionType === 'longBreak' || (currentCyclePosition === 0 && sessionCount > 0);
                
                if (isStartingNewCycle) {
                  return 'Start New Cycle';
                } else if (nextSessionType === 'work') {
                  return 'Start Work Session';
                } else if (nextSessionType === 'shortBreak') {
                  return 'Start Short Break';
                } else {
                  return 'Start Long Break';
                }
              })()}
            </Button>
            <Button 
              size="large" 
              onClick={handleSkipSession}
              style={{ 
                width: '100%',
                maxWidth: '300px',
                height: '40px',
                fontSize: '14px'
              }}
            >
              Skip for Now
            </Button>
          </Space>
        </div>
      </Modal>

      {/* Help Modal */}
      <Modal
        title="Pomodoro Timer Help"
        open={showHelp}
        onCancel={() => setShowHelp(false)}
        footer={[
          <Button key="close" onClick={() => setShowHelp(false)}>
            Close
          </Button>
        ]}
        width="min(90vw, 600px)"
        styles={{ 
          body: { 
            padding: '16px', 
            maxHeight: '70vh', 
            overflowY: 'auto',
            overflowX: 'hidden'
          },
          content: {
            maxWidth: '600px',
            margin: '0 auto'
          }
        }}
      >
        <div>
          <Title level={4}>🍅 How to Use Pomodoro Timer</Title>
          <ol style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
            <li><strong>Set Your Times:</strong> Configure work and break durations in Settings</li>
            <li><strong>Start Focus Session:</strong> Click Start to begin your work session</li>
            <li><strong>Follow the Cycle:</strong> Work → Short Break → Work → Short Break → Work → Short Break → Work → Long Break</li>
            <li><strong>Track Progress:</strong> Watch your progress in the visual cycle flow below the timer</li>
            <li><strong>Complete Cycles:</strong> After each long break, a fresh cycle begins</li>
          </ol>
          
          <Title level={4}>📊 Progress Flow Guide</Title>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
            <li><strong>Green Steps:</strong> Completed sessions with checkmarks</li>
            <li><strong>Blue/Orange/Purple:</strong> Current active session (Work/Break/Long Break)</li>
            <li><strong>Gray Steps:</strong> Upcoming sessions in the cycle</li>
            <li><strong>Cycle Completion:</strong> After 4 work sessions, take a 15-minute long break</li>
            <li><strong>Fresh Start:</strong> Each long break completion starts a new cycle</li>
          </ul>
          
          <Title level={4}>⚙️ Settings Guide</Title>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
            <li><strong>Work Duration:</strong> Time for focused work (recommended: 25 minutes)</li>
            <li><strong>Short Break:</strong> Brief rest between work sessions (recommended: 5 minutes)</li>
            <li><strong>Long Break:</strong> Extended break after multiple sessions (recommended: 15 minutes)</li>
            <li><strong>Auto-start:</strong> Automatically begin next session or break</li>
          </ul>
          
          <Title level={4}>🔔 Notifications & Sound</Title>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
            <li><strong>Browser Notifications:</strong> Get notified even when tab is not active</li>
            <li><strong>Sound Alerts:</strong> Audio notification when sessions complete</li>
            <li><strong>Quick Toggle:</strong> Enable/disable from main interface</li>
            <li><strong>Blue Background:</strong> Indicates feature is ON</li>
          </ul>
          
          <Alert 
            message="Pro Tip"
            description="Use the Pomodoro Technique to improve focus and productivity. Take breaks seriously - they help maintain mental energy!"
            type="info"
            showIcon
            style={{ marginTop: '16px' }}
          />
        </div>
      </Modal>

      {/* Settings Modal */}
      <Modal
        title="Pomodoro Settings"
        open={showSettings}
        onCancel={() => setShowSettings(false)}
        footer={null}
        width="min(90vw, 600px)"
        styles={{ 
          body: { 
            padding: '16px', 
            maxHeight: '70vh', 
            overflowY: 'auto',
            overflowX: 'hidden'
          },
          content: {
            maxWidth: '600px',
            margin: '0 auto'
          }
        }}
      >
        <Form
          layout="vertical"
          initialValues={settings}
          onFinish={handleSettingsSave}
          validateTrigger={['onChange', 'onBlur']}
          style={{ padding: '0 4px' }}
        >
          <Title level={4}>Work Duration</Title>
          <Row gutter={8}>
            <Col span={8}>
              <Form.Item 
                name={['workDuration', 'hours']} 
                label="Hours"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'number', min: 0, max: 23, message: 'Hours must be between 0-23' }
                ]}
              >
                <InputNumber min={0} max={23} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                name={['workDuration', 'minutes']} 
                label="Minutes"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'number', min: 0, max: 59, message: 'Minutes must be between 0-59' }
                ]}
              >
                <InputNumber min={0} max={59} style={{ width: '100%' }} placeholder="25" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                name={['workDuration', 'seconds']} 
                label="Seconds"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'number', min: 0, max: 59, message: 'Seconds must be between 0-59' }
                ]}
              >
                <InputNumber min={0} max={59} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Title level={4}>Short Break Duration</Title>
          <Row gutter={8}>
            <Col span={8}>
              <Form.Item 
                name={['shortBreakDuration', 'hours']} 
                label="Hours"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'number', min: 0, max: 23, message: 'Hours must be between 0-23' }
                ]}
              >
                <InputNumber min={0} max={23} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                name={['shortBreakDuration', 'minutes']} 
                label="Minutes"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'number', min: 0, max: 59, message: 'Minutes must be between 0-59' }
                ]}
              >
                <InputNumber min={0} max={59} style={{ width: '100%' }} placeholder="5" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                name={['shortBreakDuration', 'seconds']} 
                label="Seconds"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'number', min: 0, max: 59, message: 'Seconds must be between 0-59' }
                ]}
              >
                <InputNumber min={0} max={59} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Title level={4}>Long Break Duration</Title>
          <Row gutter={8}>
            <Col span={8}>
              <Form.Item 
                name={['longBreakDuration', 'hours']} 
                label="Hours"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'number', min: 0, max: 23, message: 'Hours must be between 0-23' }
                ]}
              >
                <InputNumber min={0} max={23} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                name={['longBreakDuration', 'minutes']} 
                label="Minutes"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'number', min: 0, max: 59, message: 'Minutes must be between 0-59' }
                ]}
              >
                <InputNumber min={0} max={59} style={{ width: '100%' }} placeholder="15" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                name={['longBreakDuration', 'seconds']} 
                label="Seconds"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'number', min: 0, max: 59, message: 'Seconds must be between 0-59' }
                ]}
              >
                <InputNumber min={0} max={59} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Form.Item 
            name="longBreakInterval" 
            label="Long Break Interval (work sessions)"
            rules={[
              { required: true, message: 'Required' },
              { type: 'number', min: 2, max: 10, message: 'Must be between 2-10 sessions' }
            ]}
          >
            <InputNumber min={2} max={10} style={{ width: '100%' }} 
              addonAfter="sessions" placeholder="4" />
          </Form.Item>

          <Form.Item 
            name="autoStartBreaks" 
            label="Auto-start breaks after work sessions"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item 
            name="autoStartSessions" 
            label="Auto-start work sessions after breaks"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Divider />

          <div className="settings-modal-footer">
            <Text style={{ fontSize: '12px', color: '#999' }}>
              💡 Changes are saved to your account and synced across devices
            </Text>
            
            <div className="settings-actions">
              <Tooltip title="Save Settings">
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={isLoadingSettings}
                  className="icon-button icon-button-primary"
                  icon={<SaveOutlined />}
                  size="large"
                />
              </Tooltip>
              
              <Tooltip title="Cancel">
                <Button 
                  onClick={() => setShowSettings(false)}
                  className="icon-button icon-button-default"
                  icon={<CloseOutlined />}
                  size="large"
                />
              </Tooltip>
              
              <Tooltip title="Reset to Defaults">
                <Button 
                  danger
                  onClick={handleResetSettings}
                  disabled={isLoadingSettings}
                  className="icon-button icon-button-danger"
                  icon={<ReloadOutlined />}
                  size="large"
                />
              </Tooltip>
            </div>
          </div>
        </Form>
      </Modal>

      {/* Custom Audio element for notification sound */}
      <audio ref={audioRef} preload="auto">
        <source src={notificationSound} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
    </div>
  );
};

export default PomodoroApp; 